import { Component, OnInit, OnDestroy } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, Validators, FormControl, ValidationErrors, AbstractControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { PasswordReset } from '@app/core/models/password-reset';
import { AuthenticationService } from '@app/core/services/authentication.service';
import { AlertService } from '@app/core/services/alert.service';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { AppValidators } from '@app/core/validators/app-validators';
import { ApolloError } from '@apollo/client/core';

@Component({
  selector: 'app-do-password-reset',
  templateUrl: './do-password-reset.component.html',
  styleUrls: ['./do-password-reset.component.scss']
})
export class DoPasswordResetComponent implements OnInit, OnDestroy {
  private componentDestroyed$: Subject<void> = new Subject();
  doPasswordResetForm!: FormGroup;
  resetToken!: string | null;
  loading: boolean = false;
  submitted: boolean = false;
  passwordReset$!: Observable<PasswordReset>;

  faLock = faLock;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private alertService: AlertService
  ) { }

  checkPasswords(control: AbstractControl): ValidationErrors | null {
    const pass = control.get('password')?.value;
    const repeatPassword = control.get('repeatPassword')?.value;

    return pass === repeatPassword ? null : { notSame: true };
  }

  ngOnInit() {
    this.resetToken = this.route.snapshot.paramMap.get('resetToken');

    this.doPasswordResetForm = new FormGroup({
      password: new FormControl('', [Validators.required, AppValidators.passwordValidator('medium')]),
      repeatPassword: new FormControl('', [Validators.required, AppValidators.passwordValidator('medium')])
    }, {
      validators: this.checkPasswords
    });
  }

  get f() { return this.doPasswordResetForm.controls; }

  onSubmit() {
      this.submitted = true;
      // stop here if form is invalid
      if (this.doPasswordResetForm.invalid || !this.resetToken) {
          return;
      }

      this.loading = true;

      this.authenticationService.doPasswordReset(this.resetToken, this.f.password.value)
          .pipe(
            takeUntil(this.componentDestroyed$)
          )
          .subscribe(
              data => {
                  this.loading = false;
                  this.f.password.reset();
                  this.f.repeatPassword.reset();
                  this.alertService.error($localize `Password reset was successfully, please log-in with new credentials.`, 'OK');
                  this.router.navigate(['/auth/sign-in']);
              },
              (error: ApolloError) => {
                  this.alertService.error($localize `Password reset failed.`, error.message);
                  this.loading = false;
              });
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
